import type { GameState, PuzzleModel } from '../types.ts';

/**
 * FLOW NAVIGATION — after an answer is completed, pick the next answer most likely to
 * continue the player's Flow (docs/FLOW-NAVIGATION.md).
 *
 * Pure and deterministic. It ranks with **only what the player can see**: the entries
 * on the board (typed or locked, right or wrong), which answers are solved, the grid's
 * structure, the answer just solved and the event's `opened` list. It never reads the
 * solution or checks correctness, so it can't act as a hint system (a test enforces
 * that this file never touches `model.solution` or an answer's `text`).
 *
 * Deliberately imports nothing but types, so the core engine can build
 * `nextAnswerAfterSolve` on it without a cycle.
 */

/** Every weight, in score points. Tune here; nothing else hard-codes a number. */
export interface NavigationConfig {
	/** The candidate crosses the answer just solved: the solve handed it a letter. */
	directCrossing: number;
	/** The candidate is in the solve's `opened` list (it just received a locked letter). */
	newLetter: number;
	/**
	 * The candidate crosses an answer already in the current Flow chain (other than the
	 * one just solved). Solving it would still continue the chain, so after a dead end it
	 * keeps the cascade going when there's a real reason to. Only scored for candidates
	 * that don't directly cross the solved answer (that case is `directCrossing`).
	 */
	chainCrossing: number;
	/** Scaled by filled / length (letters the player can see, typed or locked). */
	completion: number;
	/**
	 * Replaces `completion` for an answer that's full but not solved: the player has
	 * already tried every letter there and it's wrong somewhere, so it's a poor place to
	 * continue. (Negative.)
	 */
	fullUnsolved: number;
	/**
	 * Look one move ahead: for each unsolved crossing whose shared cell is still empty
	 * (solving the candidate would hand it a new letter), `futurePerCrossing` points plus
	 * `futureProgress` × that crossing's proportion filled *after* the gift — a letter
	 * that nearly completes a crossing is worth more. Capped at `futureMax`.
	 */
	futurePerCrossing: number;
	futureProgress: number;
	futureMax: number;
	/**
	 * Up to `proximity` points, falling linearly to 0 at `proximityRange` cells
	 * (Manhattan) between the candidate's landing cell (its first empty cell) and the
	 * nearest cell of the solved answer (or the cursor cell when nothing was solved).
	 */
	proximity: number;
	proximityRange: number;
	/**
	 * With a camera (`visibleCells` given): the candidate is fully on screen. Big enough
	 * to win a near-tie, small enough that a clearly better off-screen answer still wins.
	 */
	visible: number;
}

export const DEFAULT_NAVIGATION_CONFIG: NavigationConfig = {
	directCrossing: 30,
	newLetter: 20,
	chainCrossing: 12,
	completion: 20,
	fullUnsolved: -45,
	futurePerCrossing: 4,
	futureProgress: 6,
	futureMax: 20,
	proximity: 20,
	proximityRange: 10,
	visible: 8,
};

export interface NavigationContext {
	/** The answer just solved (the one whose completion triggered navigation). */
	solvedId?: string;
	/**
	 * The solve event's `opened` list. When omitted with a `solvedId`, it's derived as
	 * the solved answer's unsolved crossings (exactly what the engine emits).
	 */
	opened?: string[];
	/** The current Flow chain (answer ids), if the caller tracks Flow. */
	flowChain?: string[];
	/** Cells currently on screen (big-board camera). Omit when the whole board shows. */
	visibleCells?: ReadonlySet<number>;
	config?: Partial<NavigationConfig>;
}

export type NavigationPartLabel =
	| 'direct crossing'
	| 'new letter'
	| 'chain crossing'
	| 'completion'
	| 'already full'
	| 'future'
	| 'proximity'
	| 'visible';

export interface NavigationPart {
	label: NavigationPartLabel;
	/** Rounded to one decimal. */
	points: number;
}

export interface NavigationCandidate {
	answerId: string;
	/** Sum of `parts`, rounded to one decimal. */
	score: number;
	/** Only the non-zero parts, in a fixed order. */
	parts: NavigationPart[];
	/** The raw facts behind the parts, used for tie-breaks and the dev panel. */
	facts: {
		direct: boolean;
		newLetter: boolean;
		chain: boolean;
		filled: number;
		length: number;
		/** Unsolved crossings that solving this answer would hand a new letter. */
		futureCrossings: number;
		visible: boolean | null;
		/** Manhattan distance used for proximity (null when there's no reference). */
		distance: number | null;
	};
}

/**
 * - `flow`: the selection crosses the just-solved answer (or another answer in the
 *   current Flow chain), so solving it continues the chain.
 * - `nearby`: nothing crossing was available, so a nearby, partly filled answer.
 * - `new-area`: a dead end: a fresh start elsewhere. Flow may break here, by design.
 * - `none`: everything is solved.
 */
export type NavigationReason = 'flow' | 'nearby' | 'new-area' | 'none';

export interface NavigationDecision {
	selected: string | null;
	/** Every unsolved answer, best first. */
	candidates: NavigationCandidate[];
	reason: NavigationReason;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/**
 * Ranks every unsolved answer as the next one to select. See `NavigationConfig` for
 * the score parts. Ties go, in order, to: direct crossing, new letter, more letters
 * filled, higher future potential, visible, nearer, then `answerOrder`.
 */
export function rankNextAnswers(
	model: PuzzleModel,
	state: GameState,
	context: NavigationContext = {},
): NavigationDecision {
	const cfg: NavigationConfig = { ...DEFAULT_NAVIGATION_CONFIG, ...context.config };
	const solved = new Set(state.solvedAnswers);
	const entries = state.entries;
	const filledCount = (id: string) => model.answers[id]!.cells.filter((c) => entries[c] !== '').length;

	const solvedId = context.solvedId !== undefined && model.answers[context.solvedId] ? context.solvedId : undefined;
	const directSet = new Set(solvedId ? (model.crossings[solvedId] ?? []) : []);
	const opened = new Set(context.opened ?? (solvedId ? [...directSet].filter((id) => !solved.has(id)) : []));
	const chainIds = (context.flowChain ?? []).filter((id) => id !== solvedId && model.answers[id]);
	const chainCrossers = new Set(chainIds.flatMap((id) => model.crossings[id] ?? []));

	// Proximity reference: the solved answer's cells, else the cursor cell.
	let refCells: number[] = [];
	if (solvedId) refCells = model.answers[solvedId]!.cells;
	else if (state.cursor) {
		const cell = model.answers[state.cursor.answerId]?.cells[state.cursor.index];
		if (cell !== undefined) refCells = [cell];
	}
	const coord = (cell: number) => [Math.floor(cell / model.width), cell % model.width] as const;
	const distanceTo = (cell: number): number | null => {
		if (refCells.length === 0) return null;
		const [r, c] = coord(cell);
		let best = Infinity;
		for (const ref of refCells) {
			const [rr, rc] = coord(ref);
			best = Math.min(best, Math.abs(r - rr) + Math.abs(c - rc));
		}
		return best;
	};

	const candidates: NavigationCandidate[] = [];
	for (const id of model.answerOrder) {
		if (solved.has(id)) continue;
		const answer = model.answers[id]!;
		if (answer.cells.length === 0) continue;
		const length = answer.cells.length;
		const filled = filledCount(id);
		const direct = directSet.has(id);
		const newLetter = opened.has(id);
		const chain = !direct && chainCrossers.has(id);

		// Future: unsolved crossings that would receive a new letter from this answer.
		let futureCrossings = 0;
		let futureRaw = 0;
		for (const other of model.crossings[id] ?? []) {
			if (solved.has(other)) continue;
			const otherCells = model.answers[other]!.cells;
			const gifts = answer.cells.filter((c) => otherCells.includes(c) && entries[c] === '').length;
			if (gifts === 0) continue;
			futureCrossings++;
			const after = (filledCount(other) + gifts) / otherCells.length;
			futureRaw += cfg.futurePerCrossing + cfg.futureProgress * after;
		}
		const future = Math.min(futureRaw, cfg.futureMax);

		const landing = answer.cells.find((c) => entries[c] === '') ?? answer.cells[0]!;
		const distance = distanceTo(landing);
		const proximity =
			distance === null || cfg.proximityRange <= 0
				? 0
				: cfg.proximity * Math.max(0, 1 - distance / cfg.proximityRange);
		const visible = context.visibleCells ? answer.cells.every((c) => context.visibleCells!.has(c)) : null;

		const full = filled === length;
		const parts: NavigationPart[] = [];
		const add = (label: NavigationPartLabel, points: number) => {
			const p = round1(points);
			if (p !== 0) parts.push({ label, points: p });
		};
		if (direct) add('direct crossing', cfg.directCrossing);
		if (newLetter) add('new letter', cfg.newLetter);
		if (chain) add('chain crossing', cfg.chainCrossing);
		if (full) add('already full', cfg.fullUnsolved);
		else add('completion', cfg.completion * (filled / length));
		add('future', future);
		add('proximity', proximity);
		if (visible) add('visible', cfg.visible);

		candidates.push({
			answerId: id,
			score: round1(parts.reduce((sum, p) => sum + p.points, 0)),
			parts,
			facts: { direct, newLetter, chain, filled, length, futureCrossings, visible, distance },
		});
	}

	const order = new Map(model.answerOrder.map((id, i) => [id, i]));
	const futureOf = (c: NavigationCandidate) => c.parts.find((p) => p.label === 'future')?.points ?? 0;
	candidates.sort(
		(a, b) =>
			b.score - a.score ||
			Number(b.facts.direct) - Number(a.facts.direct) ||
			Number(b.facts.newLetter) - Number(a.facts.newLetter) ||
			b.facts.filled - a.facts.filled ||
			futureOf(b) - futureOf(a) ||
			Number(b.facts.visible === true) - Number(a.facts.visible === true) ||
			(a.facts.distance ?? Infinity) - (b.facts.distance ?? Infinity) ||
			order.get(a.answerId)! - order.get(b.answerId)!,
	);

	const top = candidates[0];
	if (!top) return { selected: null, candidates, reason: 'none' };
	const reason: NavigationReason =
		top.facts.direct || top.facts.chain
			? 'flow'
			: top.facts.filled > 0 && top.facts.filled < top.facts.length
				? 'nearby'
				: 'new-area';
	return { selected: top.answerId, candidates, reason };
}
